import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { reactRouterBrowserTracingIntegration } from '@sentry/react';

/**
 * Sentry DSN. Stored in the environment (VITE_SENTRY_DSN) so it can be
 * configured per environment without code changes. When unset, all Sentry
 * calls become no-ops so the app keeps working in local development.
 */
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export interface SentryUser {
  id?: string;
  email?: string;
  username?: string;
}

/**
 * True when a Sentry DSN has been configured.
 */
export const isSentryEnabled = (): boolean => Boolean(SENTRY_DSN);

/**
 * Initializes Sentry. Called once when the app boots, before render.
 * Route changes are instrumented so errors are automatically tagged with
 * the current URL/transaction (React Router v7 SPA).
 */
export function initSentry(): void {
  if (!isSentryEnabled()) {
    console.warn('Sentry DSN not configured; skipping Sentry init.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN as string,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      reactRouterBrowserTracingIntegration({
        // Track client-side SPA route changes from React Router v6+/v7.
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      // Capture console.log / console.warn / console.error calls as Sentry Logs.
      // Multiple arguments are parsed into searchable attributes
      // (message.parameter.N) by the SDK (requires @sentry/… >= 10.13.0).
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
  });
}

/**
 * Attaches the current user to every reported event. Used after login and on
 * every route change (mirrors identifyClarityUser). Safe to call with null to
 * clear the context on logout.
 */
export function identifySentryUser(user?: SentryUser | null): void {
  if (!isSentryEnabled() || !user?.id) return;
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username || user.email,
  });
}

/**
 * Clears the currently attached user context (e.g. on logout).
 */
export function clearSentryUser(): void {
  if (!isSentryEnabled()) return;
  Sentry.setUser(null);
}

/**
 * Captures an exception with optional extra context. Returns the event id so
 * callers can surface a support reference in the UI.
 */
export function captureSentryError(
  error: unknown,
  context?: Record<string, unknown>,
): string | undefined {
  if (!isSentryEnabled()) return undefined;
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('extra', context);
      Sentry.captureException(error);
    });
    return undefined;
  }
  return Sentry.captureException(error);
}